// useCartActions.ts
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentCluster } from "../../store/slices/currentClusterSlice";
import { SaleItemInterface } from "../types/types";
import { RootState } from "../../store/store";
import { selectCategoryList } from "../../store/slices/categoryListSlice";
import {
  addToCart,
  decreaseQuantity,
  increaseQuantity,
  removeFromCart,
} from "../../store/slices/cartSlice";
import {
  addToComplexOrderCart,
  decreaseComplexOrderCartQuantity,
  increaseComplexOrderCartQuantity,
  removeFromComplexOrderCart,
} from "../../store/slices/complexOrderCartSlice";
import { useDisplayPrice } from "./useDisplayPrice";

export const useCartActions = (item: SaleItemInterface) => {
  const cluster = useSelector(selectCurrentCluster);
  const categoryList = useSelector(selectCategoryList);
  const dispatch = useDispatch();
  const { items: cartItems } = useSelector((state: RootState) => state.cart);
  const { items: complexItems } = useSelector(
    (state: RootState) => state.complexOrderCart,
  );

  const getPrice = useDisplayPrice();

  const category = categoryList.find((c) => c.id === item.categoryId);
  const isComplexCategory = category?.isComplex ?? false;
  const hasComplexSales = cluster?.complexSales === true;
  const hasRegularSales = cluster?.regularSales !== false;

  const cartItem = cartItems.find((i) => String(i.id) === String(item.id));
  const complexItem = complexItems.find(
    (i) => i.itemId === item.id && !i.parentId,
  );

  const quantity = isComplexCategory
    ? complexItem?.quantity || 0
    : cartItem?.quantity || 0;

  const price = getPrice(item.prices);
  const hasPrice = Boolean(price) && price > 0;

  const getFirstImage = (files: string[] | undefined) => {
    if (!files || !Array.isArray(files)) return "";
    const image = files.find(
      (path) => !path.includes("video_") && !path.includes("poster_"),
    );
    if (image) return image;
    const poster = files.find((path) => path.includes("poster_"));
    if (poster) return poster;
    return files[0] || "";
  };

  const image = getFirstImage(item.files);

  const handleAddToCart = () => {
    dispatch(
      addToCart({
        id: String(item.id),
        name: item.name || "",
        price,
        image,
        minQuantity: item.minQuantity || 1,
        quantityStep: item.quantityStep || 1,
        attributes: item.attributes || [],
        unit: item.unit,
      }),
    );
  };

  const handleAddToComplex = () => {
    // Если товар уже в проекте — удаляем его
    if (complexItem) {
      dispatch(removeFromComplexOrderCart(complexItem.id));
      return;
    }

    // Добавляем родителя
    dispatch(
      addToComplexOrderCart({
        itemId: item.id,
        name: item.name || "",
        price,
        image,
        categoryId: Number(item.categoryId) || undefined,
        minQuantity: item.minQuantity || 1,
        quantityStep: item.quantityStep || 1,
        attributes: item.attributes?.reduce(
          (acc, attr) => {
            acc[attr.name] = String(attr.value);
            return acc;
          },
          {} as Record<string, any>,
        ),
        unit: item.unit, // 👈 добавил
      }),
    );

    // Добавляем детей
    const kits = item.items || [];
    if (kits.length > 0) {
      kits.forEach(
        (kitItem: {
          childItemId: number;
          quantity: number;
          item?: SaleItemInterface;
        }) => {
          const child = kitItem.item;
          if (!child) return;

          const childPrice = getPrice(child.prices);
          if (childPrice <= 0) return;

          const childImage = getFirstImage(child.files);

          dispatch(
            addToComplexOrderCart({
              itemId: child.id,
              name: child.name || "",
              price: childPrice,
              image: childImage,
              categoryId: Number(child.categoryId) || undefined,
              minQuantity: child.minQuantity || 1,
              quantityStep: child.quantityStep || 1,
              parentId: String(item.id),
              attributes: child.attributes?.reduce(
                (acc, attr) => {
                  acc[attr.name] = String(attr.value);
                  return acc;
                },
                {} as Record<string, any>,
              ),
              unit: child.unit, // 👈 добавил
            }),
          );
        },
      );
    }
  };

  const handleIncrease = () => {
    if (isComplexCategory && complexItem) {
      dispatch(increaseComplexOrderCartQuantity(complexItem.id));
    } else if (cartItem) {
      dispatch(increaseQuantity(cartItem.id));
    }
  };

  const handleDecrease = () => {
    if (isComplexCategory && complexItem) {
      dispatch(decreaseComplexOrderCartQuantity(complexItem.id));
    } else if (cartItem) {
      dispatch(decreaseQuantity(cartItem.id));
    }
  };

  const handleRemove = () => {
    if (isComplexCategory && complexItem) {
      dispatch(removeFromComplexOrderCart(complexItem.id));
    } else if (cartItem) {
      dispatch(removeFromCart(cartItem.id));
    }
  };

  return {
    quantity,
    isComplexCategory,
    showComplexButton: hasComplexSales,
    showCartButton: isComplexCategory ? false : hasRegularSales && hasPrice,
    isInComplexCart: !!complexItem,
    handleAddToCart,
    handleAddToComplex,
    handleIncrease,
    handleDecrease,
    handleRemove,
  };
};